/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Form } from '@ps/web-martech-cms-types';
import type { DateTimePickerBlock } from '@ps/web-martech-cms-types/Form';
import { format, isValid, parse } from 'date-fns';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import createForm from '@swf/components/Forms/Form';
import RawButton from '@swf/components/StarsButton/RawButton';

import styles from './CMSForm.scss';
import BlockLoader from './FormBlocks/BlockLoader';
import ReCaptcha from './ReCaptcha/ReCaptha';

const TheForm = createForm<Form>();

declare global {
  interface Window {
    grecaptcha: {
      reset(): void;
    };
  }
}

interface FlatPickerElement extends HTMLInputElement {
  _flatpickr: {
    clear(triggerChangeEvent?: boolean, toInitial?: boolean): void;
  };
}

const getFormattersAndDatePickers = (modular_blocks: Form['modular_blocks']) => {
  const formatters = {};
  const datePickers: string[] = [];

  modular_blocks.forEach((mb) => {
    const [blockType] = Object.keys(mb);
    if (blockType === 'date_time_picker') {
      const { submit_date_format, field_name } = (mb as DateTimePickerBlock).date_time_picker;
      datePickers.push(field_name);

      if (submit_date_format) {
        const dateFormatter = (value: string) => {
          const submitFormatDate = parse(value, submit_date_format, new Date());
          if (isValid(submitFormatDate)) return value;

          const date = parse(value, 'yyyy-MM-dd', new Date());
          return isValid(date) ? format(date, submit_date_format) : value;
        };

        formatters[field_name] = dateFormatter;
      }
    }
  });

  return { formatters, datePickers };
};

const getFormData = (formValues: any, formatters: object) => {
  const data = new FormData();
  for (const fieldName in formValues) {
    const fieldFormatter = formatters[fieldName];
    if (fieldFormatter) {
      formValues[fieldName] = fieldFormatter(formValues[fieldName]);
    }
    data.append(fieldName, formValues[fieldName]);
  }
  return data;
};

const CMSForm: React.FC<Form> = ({
  api_url,
  submit_button_label,
  modular_blocks,
  recaptcha,
  content_type,
  clear_on_successful_submit,
  submit_success_message,
  submit_error_message
}) => {
  const hasRecaptcha = !!(recaptcha.active && recaptcha.site_key && recaptcha.secret_key);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [submitMessage, setSubmitMessage] = useState({ content: '', error: false });
  const { push } = useHistory();

  const handleSubmit = async (formValues: any, form: any) => {
    formValues['g-recaptcha-response'] = recaptchaToken;
    const { formatters, datePickers } = getFormattersAndDatePickers(modular_blocks);

    let data = getFormData(formValues, formatters);
    if (content_type === 'application/json') {
      data = { ...formValues };
    }

    try {
      const response = await fetch(api_url, {
        method: 'POST',
        headers: {
          'Content-Type': content_type || 'multipart/form-data'
        },
        body: data
      });

      if (response.ok) {
        if (formValues.redirect) {
          push(formValues.redirect);
        } else {
          if (clear_on_successful_submit) {
            datePickers.forEach((fieldId: string) => {
              const datePicker = document.getElementById(fieldId) as FlatPickerElement;
              // eslint-disable-next-line no-underscore-dangle
              datePicker?._flatpickr?.clear?.();
            });
            form.restart();
            window?.grecaptcha?.reset?.();
          }
          setSubmitMessage({ content: submit_success_message, error: false });
        }
      } else {
        throw new Error('Error submitting the form.');
      }
    } catch (err) {
      setSubmitMessage({ content: submit_error_message, error: true });
    }
  };

  return (
    <TheForm
      className={styles.form}
      onSubmit={handleSubmit}
      render={({ pristine, hasValidationErrors }) => (
        <>
          {modular_blocks.map((item, key) => (
            <BlockLoader key={key} blockType={Object.keys(item)[0]} data={item} />
          ))}
          {hasRecaptcha && (
            <ReCaptcha
              siteKey={recaptcha.site_key as string}
              errorMessage={recaptcha.error_message}
              recaptchaToken={recaptchaToken}
              setRecaptchaToken={setRecaptchaToken}
            />
          )}
          <RawButton
            className={styles.submitBtn}
            block
            disabled={pristine || hasValidationErrors || (hasRecaptcha && !recaptchaToken)}
            type="submit"
          >
            {submit_button_label}
          </RawButton>
          <p className={submitMessage.error ? styles.error : styles.success}>
            {submitMessage.content}
          </p>
        </>
      )}
    />
  );
};

export default CMSForm;
